import cx from "classnames";
import Stripe from "stripe";
import { set } from "date-fns";
import isEmpty from "lodash/isEmpty";
import isNull from "lodash/isNull";
import orderBy from "lodash/orderBy";
import { memo } from "react";

import s from "./WLStripePaymentMethods.module.scss";
import useStripePaymentMethodStore from "./store";
import usePaymentMethodsProvider from "./setupProviders/usePaymentMethodsProvider";
import useExistingStripeCustomerProvider from "./setupProviders/useExistingStripeCustomerProvider";
import useExistingStripeSubscriptionProvider from "./setupProviders/useExistingStripeSubscriptionProvider";
import useSetupDefaultPaymentMethodsProvider from "./setupProviders/useSetupDefaultPaymentMethodsProvider";
import useStripePaymentMethodsActionsProvider from "./useStripePaymentMethodsActionsProvider";
import { loadStripe } from "@stripe/stripe-js";
import AddStripeCard from "./AddStripeCard/AddStripeCard";
import { Elements } from "@stripe/react-stripe-js";
import {
  IonButton,
  IonIcon,
  IonItem,
  IonLabel,
  IonSpinner,
  IonToast,
  useIonActionSheet,
} from "@ionic/react";
import { addOutline, checkmarkCircle, ellipsisVertical } from "ionicons/icons";

import { stripeKey } from "../../firebase";
import { useFormsStore } from "../../../Common/store/common";
import { formatDate, useBreakpoint } from "../../../Common/utils/common";
import { WLCOLORS } from "../../../Common/config/global";
import { getStripeCardsLogo } from "../../../Common/constants/subscriptionConst";

const stripePromise = loadStripe(stripeKey);
const WLStripePaymentMethods = ({
  darkMode,
  hasOtherPaymentMethod,
}: {
  darkMode?: boolean;
  hasOtherPaymentMethod?: boolean;
}) => {
  const [present] = useIonActionSheet();
  const isMobile = useBreakpoint() === "mobile";

  usePaymentMethodsProvider();
  useExistingStripeCustomerProvider();
  useExistingStripeSubscriptionProvider();
  useSetupDefaultPaymentMethodsProvider();

  const { stripeShowAddCard, presetCardOptions } =
    useStripePaymentMethodsActionsProvider({ hasOtherPaymentMethod });

  const {
    defaultPaymentMethodId,

    paymentMethods,
    setStripeShowAddCard,

    selectedPaymentMethod,
    setSelectedPaymentMethod,

    useOtherPaymentMethod,

    error,
    setError,
  } = useStripePaymentMethodStore();

  const { formLoading } = useFormsStore();

  return (
    <div className={s.container}>
      <IonItem
        onClick={() => {
          setStripeShowAddCard(true);
        }}
        disabled={formLoading}
        className={cx(
          s.addPaymentMethod,
          "ion-no-padding",
          isMobile ? "win-header-5" : "win-header-2",
          "medium",
          "gilroy",
          darkMode && "white",
        )}
        detail={false}
        button
      >
        <IonLabel>Payment Method</IonLabel>
        <IonIcon
          className={s.addIcon}
          icon={addOutline}
          color={!!darkMode ? "light" : "dark"}
        />
      </IonItem>
      {stripeShowAddCard ? (
        <Elements stripe={stripePromise}>
          <AddStripeCard darkMode={!!darkMode} />
        </Elements>
      ) : paymentMethods === undefined ? (
        <></>
      ) : isNull(paymentMethods) ? (
        <></>
      ) : (
        <>
          {orderBy(
            paymentMethods || [],
            (method) =>
              !isNull(defaultPaymentMethodId) &&
              defaultPaymentMethodId === method.id,
            "desc",
          ).map((method) => {
            const isDefault =
              !isNull(defaultPaymentMethodId) &&
              defaultPaymentMethodId === method.id;

            const selected =
              selectedPaymentMethod !== undefined &&
              !isNull(selectedPaymentMethod) &&
              selectedPaymentMethod.id === method.id;

            const card = method.card! as Stripe.PaymentMethod.Card;
            return (
              <IonItem
                key={`payment-method-item-${method.id}`}
                onClick={() => {
                  setSelectedPaymentMethod(method);
                }}
                detail={false}
                className={cx(s.paymentMethod, "ion-no-padding win-sub")}
                button
                lines="full"
              >
                <img
                  className={s.methodCardImg}
                  src={getStripeCardsLogo(card.brand)}
                  slot="start"
                  alt=""
                  loading="lazy"
                  decoding="async"
                />

                <div
                  className={cx(
                    `${isMobile ? "win-sub" : "win-header-4"} ${darkMode && "white"}`,
                  )}
                >
                  <IonLabel>
                    **** **** **** {card.last4} • Exp:{" "}
                    {formatDate(
                      set(new Date(), { month: card.exp_month - 1 }).getTime(),
                      "MMM",
                    )}{" "}
                    {card.exp_year}
                  </IonLabel>
                </div>

                {selected && (
                  <IonIcon
                    className={s.checkmarkCircleIcon}
                    slot="end"
                    icon={checkmarkCircle}
                    color="success"
                  />
                )}

                {/* The default card may be billing an active membership, so it is
                    managed by promoting another card first, never directly. */}
                {paymentMethods.length > 1 && !isDefault && (
                  <IonButton
                    slot="end"
                    className={cx(s.optionButton, "ion-no-margin ion-no-padding")}
                    color={WLCOLORS.dark}
                    fill="clear"
                    onClick={(event) => {
                      event.stopPropagation();
                      if (method.card !== undefined) {
                        presetCardOptions(
                          method.card.last4,
                          isDefault,
                          method.id,
                          present,
                        );
                      }
                    }}
                  >
                    <IonIcon
                      icon={ellipsisVertical}
                      color={!!darkMode ? "light" : "dark"}
                    />
                  </IonButton>
                )}
              </IonItem>
            );
          })}

          {hasOtherPaymentMethod !== false && (
            <IonItem
              onClick={() => {
                if (!isNull(useOtherPaymentMethod)) {
                  useOtherPaymentMethod();
                }
              }}
              disabled={formLoading || isNull(useOtherPaymentMethod)}
              className={cx(
                s.addPaymentMethod,
                `ion-no-padding ${isMobile ? "win-sub" : "win-header-4"} regular gilroy ${darkMode && "white"}`,
              )}
              detail={false}
              lines="full"
              button
            >
              <IonLabel>Use other payment method</IonLabel>
              {!formLoading ? (
                <IonIcon
                  slot="start"
                  className={s.addIcon}
                  icon={addOutline}
                  color={!!darkMode ? "light" : "dark"}
                />
              ) : (
                <IonSpinner
                  name="circular"
                  slot="start"
                  color={!!darkMode ? "light" : "dark"}
                />
              )}
            </IonItem>
          )}
        </>
      )}

      <IonToast
        isOpen={!isEmpty(error)}
        message={error}
        duration={3000}
        color={WLCOLORS.danger}
        onDidDismiss={() => setError("")}
      />
    </div>
  );
};

export default memo(WLStripePaymentMethods);
